import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { listMyInvites, respondToInvite } from '../api/owner'
import { LoadingScreen } from '../components/LoadingScreen'
import { formatEventDate } from '../lib/format'

/**
 * Alle uitnodigingen die op jouw adres binnenkwamen, op één scherm.
 *
 * De banner op "mijn events" toont ze ook, maar die staat er alleen als je daar
 * bent en verdwijnt zodra je geantwoord hebt. Dit is de plek waar je ze kan gaan
 * zoeken — vanuit het menu, van waar je ook bent.
 *
 * Alleen openstaande. Wat je aangenomen hebt staat als event in je lijst, en wat
 * je geweigerd hebt is klaar; een archief van afgeslagen uitnodigingen is een
 * logboek waar niemand ooit in kijkt.
 */
export function MyInvitesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const invites = useQuery({
    queryKey: ['owner', 'invites'],
    queryFn: listMyInvites,
  })

  const respond = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) => respondToInvite(id, accept),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['owner', 'invites'] })
      await queryClient.invalidateQueries({ queryKey: ['owner', 'events'] })
    },
  })

  if (invites.isPending) {
    return <LoadingScreen label="Uitnodigingen ophalen…" />
  }

  const list = invites.data?.invites ?? []

  return (
    <div className="flex min-h-full flex-col lg:h-full">
      <div className="flex items-start justify-between px-5.5 pt-6.5 lg:px-12 lg:pt-10">
        <div>
          <button
            type="button"
            onClick={() => navigate('/beheer')}
            className="text-ink-soft hover:text-ink mb-3 flex cursor-pointer items-center gap-2 text-sm transition-colors"
          >
            <span aria-hidden="true">←</span>
            Mijn events
          </button>

          <h1 className="font-display text-[38px] leading-[1.05] lg:text-[46px]">Uitnodigingen</h1>
        </div>
      </div>

      <div className="flex-1 px-5.5 pt-6 pb-12 lg:px-12 lg:pt-8">
        {list.length === 0 ? (
          <p className="text-ink-soft max-w-[420px] text-[15px] leading-[1.6] text-pretty">
            Er staat niets open. Nodigt iemand je uit om een feest mee te beheren, dan komt het hier
            te staan — en krijg je er een mail over.
          </p>
        ) : (
          <div className="flex flex-col gap-3 lg:max-w-[560px]">
            {list.map((invite) => (
              <div
                key={invite.id}
                className="rounded-card border-line-card bg-surface border p-4.5 lg:p-5"
              >
                <p className="text-[15px] leading-[1.5] lg:text-base">
                  <span className="font-semibold">{invite.invitedBy}</span> vraagt je om{' '}
                  <span className="font-semibold">{invite.eventTitle}</span> mee te beheren.
                </p>
                <p className="text-ink-faint mt-1 text-[13px]">
                  Uitgenodigd op {formatEventDate(invite.createdUtc)}
                </p>

                <p className="text-ink-soft mt-3 text-[13px] leading-[1.5] text-pretty">
                  Neem je aan, dan kan je alles wat de maker kan — behalve zelf mensen uitnodigen of
                  verwijderen.
                </p>

                <div className="mt-4 flex gap-2.5">
                  <button
                    type="button"
                    disabled={respond.isPending}
                    onClick={() => respond.mutate({ id: invite.id, accept: true })}
                    className="bg-accent text-accent-ink h-11.5 flex-1 cursor-pointer rounded-full text-[15px] font-semibold transition hover:brightness-95 disabled:opacity-55"
                  >
                    Aannemen
                  </button>
                  <button
                    type="button"
                    disabled={respond.isPending}
                    onClick={() => respond.mutate({ id: invite.id, accept: false })}
                    className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink h-11.5 cursor-pointer rounded-full border px-5 text-[15px] transition-colors disabled:opacity-55"
                  >
                    Weigeren
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
